import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import { getAnalyzeSession, getProject } from "../utils/api";

// 30-min Calendly event for "we built a model, now let's get it into production"
// conversations. Pre-filled with the project name via ?a1= so the seller knows
// which project they're discussing before the call starts.
const DEPLOYMENT_CALL_URL = "https://calendly.com/assertion-ai/deployment-call";

export default function AnalyzeReport() {
  const { sessionId, projectId } = useParams();
  const navigate = useNavigate();
  const chatPath = projectId
    ? `/projects/${projectId}/analyze/${sessionId}`
    : `/analyze/${sessionId}`;
  const theme = useTheme();

  const [reportHtml, setReportHtml] = useState(null);
  const [filename, setFilename] = useState("");
  const [projectName, setProjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!sessionId) return;
    getAnalyzeSession(sessionId)
      .then((session) => {
        if (!session.report_html) {
          setError("Report not ready yet. Go back and continue the analysis.");
        } else {
          setReportHtml(session.report_html);
          setFilename(session.filename || "");
        }
      })
      .catch((e) => {
        setError(e?.response?.data?.error || e.message || "Failed to load report");
      })
      .finally(() => setLoading(false));
  }, [sessionId]);

  // Resolve project name for the deployment-call Calendly prefill. Cheap
  // (one Firestore read), and if it fails we just fall back to the filename.
  useEffect(() => {
    if (!projectId) return;
    getProject(projectId)
      .then((p) => setProjectName(p?.name || ""))
      .catch(() => { /* fall back to filename via the link builder */ });
  }, [projectId]);

  const deploymentCallHref = (() => {
    const label = projectName || filename || "this project";
    return `${DEPLOYMENT_CALL_URL}?a1=${encodeURIComponent(label)}`;
  })();

  // Download the report as a self-contained .html file. The HTML is already in
  // memory (reportHtml), so this is a client-only Blob download — no API call.
  const handleDownload = () => {
    if (!reportHtml) return;
    const base = (projectName || filename || "analysis-report")
      .replace(/[^a-z0-9-_]+/gi, "_")
      .replace(/^_+|_+$/g, "") || "analysis-report";
    const blob = new Blob([reportHtml], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${base}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 80px)" }}>
      {/* Top bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => navigate(chatPath)}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Back
          </Button>
          <AutoGraphIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={600}>
            Analysis Report
          </Typography>
          {filename && (
            <Chip label={filename} size="small" variant="outlined" sx={{ fontSize: 11 }} />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {reportHtml && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              Download
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            href={deploymentCallHref}
            target="_blank"
            rel="noopener"
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Book a deployment call
          </Button>
        </Box>
      </Box>

      {/* Report content */}
      <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ p: 4, maxWidth: 520, mx: "auto" }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(chatPath)}>
              Back to analysis
            </Button>
          </Box>
        )}

        {!loading && reportHtml && (
          <iframe
            srcDoc={reportHtml}
            title="Analysis Report"
            sandbox="allow-scripts allow-same-origin allow-downloads"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
            }}
          />
        )}
      </Box>
    </Box>
  );
}
